import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api/api';
import Loader from '../components/Loader';
import { 
  FaChartLine, 
  FaRupeeSign, 
  FaReceipt, 
  FaUsers, 
  FaCalendar,
  FaArrowUp,
  FaArrowDown,
  FaChartBar,
  FaChartPie,
  FaDownload,
  FaFilter,
  FaCalendarAlt,
  FaLightbulb,
  FaWallet,
  FaPercentage,
  FaAngleUp,
  FaAngleDown
} from 'react-icons/fa';

// Import Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const Analytics = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    stats: {
      totalReceipts: 0,
      currentMonthAmount: 0,
      currentMonthReceipts: 0,
      previousMonthAmount: 0,
      previousMonthReceipts: 0,
      amountChange: 0,
      receiptChange: 0
    },
    paymentModeStats: []
  });
  const [analytics, setAnalytics] = useState([]);
  const [tenantStats, setTenantStats] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [activeInsight, setActiveInsight] = useState(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedYear]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const [statsRes, analyticsRes, tenantRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get(`/dashboard/analytics?year=${selectedYear}`),
        api.get('/dashboard/tenants')
      ]);
      
      console.log('Analytics API Response:', {
        stats: statsRes.data,
        analytics: analyticsRes.data,
        tenantStats: tenantRes.data
      });
      
      setStats(statsRes.data || {
        stats: {
          totalReceipts: 0,
          currentMonthAmount: 0,
          currentMonthReceipts: 0,
          previousMonthAmount: 0,
          previousMonthReceipts: 0,
          amountChange: 0,
          receiptChange: 0
        },
        paymentModeStats: []
      });
      setAnalytics(analyticsRes.data || []);
      setTenantStats(tenantRes.data || []);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      setStats({
        stats: {
          totalReceipts: 0,
          currentMonthAmount: 0,
          currentMonthReceipts: 0,
          previousMonthAmount: 0,
          previousMonthReceipts: 0,
          amountChange: 0,
          receiptChange: 0
        },
        paymentModeStats: []
      });
      setAnalytics([]);
      setTenantStats([]);
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  const exportData = (format) => {
    const data = {
      stats,
      analytics,
      tenantStats,
      exportedAt: new Date().toISOString()
    };
    
    if (format === 'csv') {
      let csv = 'Month,Receipt Count,Total Amount,Average Amount\n';
      analytics.forEach(item => {
        csv += `${item.month || ''},${item.receiptCount || 0},${item.totalAmount || 0},${item.averageAmount || 0}\n`;
      });
      
      const dataUri = 'data:text/csv;charset=utf-8,'+ encodeURIComponent(csv);
      const link = document.createElement('a');
      link.href = dataUri;
      link.download = `pg-analytics-${selectedYear}.csv`;
      link.click();
    }
  };

  // Calculate insights (without top tenant)
  const insights = useMemo(() => {
    if (!analytics.length) {
      return [
        {
          id: 1,
          title: 'Revenue Peak',
          value: '₹0',
          description: 'No data available',
          icon: FaChartLine,
          color: 'from-gray-500 to-gray-600',
          bgColor: 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700',
          textColor: 'text-gray-700 dark:text-gray-300'
        },
        {
          id: 2,
          title: 'Avg Receipt Value',
          value: '₹0',
          description: 'No data available',
          icon: FaAngleUp,
          color: 'from-gray-500 to-gray-600',
          bgColor: 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700',
          textColor: 'text-gray-700 dark:text-gray-300'
        },
        {
          id: 3,
          title: 'Growth Rate',
          value: '0%',
          description: 'No data available',
          icon: FaPercentage,
          color: 'from-gray-500 to-gray-600',
          bgColor: 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700',
          textColor: 'text-gray-700 dark:text-gray-300'
        }
      ];
    }
    
    const highestMonth = analytics.reduce((max, curr) => 
      (curr.totalAmount || 0) > (max.totalAmount || 0) ? curr : max, analytics[0]
    );
    
    const averageReceiptValue = (stats?.stats?.currentMonthAmount || 0) / Math.max(stats?.stats?.currentMonthReceipts || 1, 1);
    const previousAverage = (stats?.stats?.previousMonthAmount || 0) / Math.max(stats?.stats?.previousMonthReceipts || 1, 1);
    const avgChange = previousAverage > 0 ? ((averageReceiptValue - previousAverage) / previousAverage) * 100 : 0;
    
    return [
      {
        id: 1,
        title: 'Revenue Peak',
        value: `₹${(highestMonth.totalAmount || 0).toLocaleString('en-IN')}`,
        description: `Highest revenue in ${highestMonth.month || 'No data'}`,
        icon: FaChartLine,
        color: 'from-emerald-500 to-green-500',
        bgColor: 'bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20',
        textColor: 'text-emerald-700 dark:text-emerald-400'
      },
      {
        id: 2,
        title: 'Avg Receipt Value',
        value: `₹${averageReceiptValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
        description: avgChange >= 0 ? `Up ${avgChange.toFixed(1)}% from last month` : `Down ${Math.abs(avgChange).toFixed(1)}% from last month`,
        icon: avgChange >= 0 ? FaAngleUp : FaAngleDown,
        color: avgChange >= 0 ? 'from-blue-500 to-cyan-500' : 'from-orange-500 to-amber-500',
        bgColor: avgChange >= 0 ? 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20' : 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20',
        textColor: avgChange >= 0 ? 'text-blue-700 dark:text-blue-400' : 'text-amber-700 dark:text-amber-400'
      },
      {
        id: 3,
        title: 'Growth Rate',
        value: `${Math.abs(stats?.stats?.amountChange || 0)}%`,
        description: (stats?.stats?.amountChange || 0) >= 0 ? 'Revenue increased' : 'Revenue decreased',
        icon: (stats?.stats?.amountChange || 0) >= 0 ? FaPercentage : FaAngleDown,
        color: (stats?.stats?.amountChange || 0) >= 0 ? 'from-green-500 to-teal-500' : 'from-rose-500 to-pink-500',
        bgColor: (stats?.stats?.amountChange || 0) >= 0 ? 'bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20' : 'bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20',
        textColor: (stats?.stats?.amountChange || 0) >= 0 ? 'text-green-700 dark:text-green-400' : 'text-rose-700 dark:text-rose-400'
      }
    ];
  }, [analytics, stats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader size="large" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading your analytics dashboard...</p>
        </div>
      </div>
    );
  }

  // Prepare chart data with dark mode colors
  const revenueChartData = {
    labels: analytics.length > 0 
      ? analytics.map(a => a.month?.substring(0, 3) || '')
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Total Revenue (₹)',
        data: analytics.length > 0 
          ? analytics.map(a => a.totalAmount || 0)
          : Array(12).fill(0),
        borderColor: theme === 'dark' ? 'rgb(129, 140, 248)' : 'rgb(99, 102, 241)',
        backgroundColor: theme === 'dark' ? 'rgba(129, 140, 248, 0.1)' : 'rgba(99, 102, 241, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: theme === 'dark' ? 'rgb(129, 140, 248)' : 'rgb(99, 102, 241)',
        pointBorderColor: theme === 'dark' ? '#1f2937' : '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
      {
        label: 'Receipt Count',
        data: analytics.length > 0 
          ? analytics.map(a => a.receiptCount || 0)
          : Array(12).fill(0),
        borderColor: theme === 'dark' ? 'rgb(52, 211, 153)' : 'rgb(16, 185, 129)',
        backgroundColor: theme === 'dark' ? 'rgba(52, 211, 153, 0.1)' : 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1',
        pointBackgroundColor: theme === 'dark' ? 'rgb(52, 211, 153)' : 'rgb(16, 185, 129)',
        pointBorderColor: theme === 'dark' ? '#1f2937' : '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const receiptTrendData = {
    labels: analytics.length > 0 
      ? analytics.map(a => a.month?.substring(0, 3) || '')
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Receipts Generated',
        data: analytics.length > 0 
          ? analytics.map(a => a.receiptCount || 0)
          : Array(12).fill(0),
        backgroundColor: theme === 'dark' ? 'rgba(167, 139, 250, 0.8)' : 'rgba(139, 92, 246, 0.8)',
        borderColor: theme === 'dark' ? 'rgb(167, 139, 250)' : 'rgb(139, 92, 246)',
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const paymentMethodData = {
    labels: stats?.paymentModeStats?.length > 0 
      ? stats.paymentModeStats.map(p => p._id?.toUpperCase() || 'Unknown')
      : ['No Data'],
    datasets: [
      {
        label: 'Payment Methods',
        data: stats?.paymentModeStats?.length > 0 
          ? stats.paymentModeStats.map(p => p.count || 0)
          : [0],
        backgroundColor: [
          'rgba(59, 130, 246, 0.9)',
          'rgba(16, 185, 129, 0.9)',
          'rgba(245, 158, 11, 0.9)',
          'rgba(239, 68, 68, 0.9)',
          'rgba(139, 92, 246, 0.9)',
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(239, 68, 68)',
          'rgb(139, 92, 246)',
        ],
        borderWidth: 2,
        hoverOffset: 20,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            family: "'Inter', sans-serif"
          },
          padding: 20,
          usePointStyle: true,
          color: theme === 'dark' ? '#e5e7eb' : '#374151',
        }
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(17, 24, 39, 0.9)',
        titleColor: '#f3f4f6',
        bodyColor: '#f3f4f6',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            if (isNaN(value)) return 'No data';
            
            if (context.dataset.label.includes('Revenue') || context.dataset.label.includes('Paid')) {
              return `₹${value.toLocaleString('en-IN')}`;
            }
            return `${context.dataset.label}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: theme === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.5)'
        },
        ticks: {
          callback: (value) => {
            if (isNaN(value)) return '₹0';
            return `₹${value.toLocaleString('en-IN')}`;
          },
          font: {
            size: 11
          },
          color: theme === 'dark' ? '#9ca3af' : '#374151'
        }
      },
      x: {
        grid: {
          color: theme === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.3)'
        },
        ticks: {
          font: {
            size: 11
          },
          color: theme === 'dark' ? '#9ca3af' : '#374151'
        }
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: {
            size: 11
          },
          color: theme === 'dark' ? '#9ca3af' : '#374151'
        }
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6 lg:p-8 transition-colors duration-300">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md">
                <FaChartLine className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
                Analytics Dashboard
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 ml-1">Deep insights into your PG revenue and performance metrics</p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
            <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm px-4 py-3 border border-gray-200 dark:border-gray-700">
              <FaCalendarAlt className="text-gray-400 dark:text-gray-500" />
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(parseInt(e.target.value))}
                className="bg-transparent border-none focus:ring-0 text-gray-700 dark:text-gray-300 font-medium"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => exportData('csv')}
                className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <FaDownload />
                <span className="font-medium">CSV</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          {
            title: 'Total Revenue',
            value: `₹${(stats?.stats?.currentMonthAmount || 0).toLocaleString('en-IN')}`,
            change: stats?.stats?.amountChange || 0,
            icon: FaRupeeSign,
            iconBg: 'from-emerald-500 to-green-500',
            cardBg: 'bg-gradient-to-br from-white to-emerald-50 dark:from-gray-800 dark:to-emerald-900/20',
            trendIcon: FaAngleUp,
            trendColor: 'text-emerald-600 dark:text-emerald-400'
          },
          {
            title: 'Total Receipts',
            value: stats?.stats?.totalReceipts || 0,
            change: stats?.stats?.receiptChange || 0,
            icon: FaReceipt,
            iconBg: 'from-blue-500 to-cyan-500',
            cardBg: 'bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/20',
            trendIcon: FaAngleUp,
            trendColor: 'text-blue-600 dark:text-blue-400'
          },
          {
            title: 'Active Tenants',
            value: tenantStats.length,
            icon: FaUsers,
            iconBg: 'from-purple-500 to-violet-500',
            cardBg: 'bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20',
            // show total tenants
          },
          {
            title: 'Avg. Receipt Value',
            value: `₹${(
              (stats?.stats?.currentMonthAmount || 0) / 
              Math.max(stats?.stats?.currentMonthReceipts || 1, 1)
            ).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
            icon: FaChartLine,
            iconBg: 'from-amber-500 to-orange-500',
            cardBg: 'bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-amber-900/20',
            month: `${months[new Date().getMonth()]} ${selectedYear}`
          }
        ].map((card, index) => (
          <div key={index} className={`${card.cardBg} rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{card.title}</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{card.value}</p>
              </div>
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.iconBg} shadow-md`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
            </div>
            
            <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
              {card.change !== undefined ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <card.trendIcon className={`${card.trendColor} mr-2`} />
                    <span className={`text-sm font-semibold ${card.trendColor}`}>
                      {Math.abs(card.change)}%
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">from last month</span>
                </div>
              ) : card.month ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaCalendar className="text-amber-500 dark:text-amber-400 mr-2" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{card.month}</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaUsers className="text-purple-500 dark:text-purple-400 mr-2" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Total tenants</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Insights (now 3 cards) */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <FaLightbulb className="h-5 w-5 text-amber-500" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Quick Insights</h2>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">Real-time analytics</span>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {insights.map((insight) => (
            <div
              key={insight.id}
              className={`${insight.bgColor} rounded-xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer transform hover:-translate-y-1`}
              onClick={() => setActiveInsight(activeInsight === insight.id ? null : insight.id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{insight.title}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{insight.value}</p>
                </div>
                <div className={`p-2 rounded-lg bg-gradient-to-br ${insight.color}`}>
                  <insight.icon className="h-4 w-4 text-white" />
                </div>
              </div>
              <p className={`text-sm ${insight.textColor} font-medium`}>
                {insight.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Revenue vs Receipts Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Revenue vs Receipts Trend</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Monthly comparison for {selectedYear}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-indigo-500 dark:bg-indigo-400"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Revenue</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 dark:bg-emerald-400"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Receipts</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-72 md:h-80">
              <Line 
                data={revenueChartData}
                options={chartOptions}
              />
            </div>
          </div>
        </div>

        {/* Receipt Trends Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Receipt Generation</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Monthly receipt count distribution</p>
              </div>
              <div className="text-sm px-3 py-1 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 rounded-full font-medium">
                {analytics.reduce((sum, month) => sum + (month.receiptCount || 0), 0)} Total Receipts
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-72 md:h-80">
              <Bar 
                data={receiptTrendData}
                options={{
                  ...chartOptions,
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: theme === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.5)'
                      },
                      ticks: {
                        callback: (value) => isNaN(value) ? '0' : value,
                        color: theme === 'dark' ? '#9ca3af' : '#374151'
                      }
                    },
                    x: {
                      grid: {
                        color: theme === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.3)'
                      },
                      ticks: {
                        color: theme === 'dark' ? '#9ca3af' : '#374151'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods Chart (full width) */}
      <div className="mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Payment Methods</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Distribution across payment modes</p>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {(stats?.paymentModeStats || []).reduce((sum, p) => sum + (p.count || 0), 0)} transactions
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-72 md:h-80">
              <Doughnut 
                data={paymentMethodData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        font: {
                          size: 11,
                          family: "'Inter', sans-serif"
                        },
                        padding: 20,
                        usePointStyle: true,
                        color: theme === 'dark' ? '#e5e7eb' : '#374151',
                      }
                    },
                    tooltip: {
                      backgroundColor: theme === 'dark' ? 'rgba(31, 41, 55, 0.9)' : 'rgba(17, 24, 39, 0.9)',
                      titleColor: '#f3f4f6',
                      bodyColor: '#f3f4f6',
                      padding: 12,
                      cornerRadius: 8,
                      displayColors: true,
                      callbacks: {
                        label: (context) => `${context.label}: ${context.parsed} receipts`
                      }
                    }
                  },
                  cutout: '65%',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Breakdown Table (full width) */}
      <div className="mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Monthly Breakdown - {selectedYear}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700">
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Month</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Receipts</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Revenue</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Average</th>
                </tr>
              </thead>
              <tbody>
                {analytics.length > 0 ? (
                  analytics.map((monthData, index) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-700 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors duration-150">
                      <td className="p-4 font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-3 ${
                            (monthData.receiptCount || 0) > 10 ? 'bg-green-500' :
                            (monthData.receiptCount || 0) > 5 ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                          }`}></div>
                          {monthData.month || 'Unknown'}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700 dark:text-gray-300 font-medium">{monthData.receiptCount || 0}</span>
                          {index > 0 && analytics[index - 1] && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              (monthData.receiptCount || 0) > (analytics[index - 1].receiptCount || 0) 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                            }`}>
                              {(monthData.receiptCount || 0) > (analytics[index - 1].receiptCount || 0) ? '↗' : '↘'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-bold text-green-600 dark:text-green-400">
                          ₹{(monthData.totalAmount || 0).toLocaleString('en-IN')}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-gray-700 dark:text-gray-300 font-medium">
                          ₹{(monthData.averageAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="p-8 text-center text-gray-500 dark:text-gray-400">
                      No analytics data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
        <p>Analytics dashboard last updated • {new Date().toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}</p>
      </div>
    </div>
  );
};

export default Analytics;